import React from 'react';

import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { palette, lightPalette } from '@app/theme';
import { ScrollableTable } from '@components/ui/ScrollableTable';


import { getScoreLevel, tableHeaderCellSx, zebraRowSx } from '../analysisReport.utilities';

import type { ScoreLevel } from '../analysisReport.utilities';
import type { ScoreCriterion } from '@api/aiReviews/api.aiReviews.reviewTypes';

const LEVEL_COLORS: Record<ScoreLevel, string> = {
  success: palette.success.main,
  warning: palette.warning.main,
  error:   palette.error.main,
};

const LEVEL_PROGRESS_SX: Record<ScoreLevel, object> = {
  success: {
    '& .MuiLinearProgress-bar': { backgroundColor: palette.success.main },
    backgroundColor: palette.success.light,
  },
  warning: {
    '& .MuiLinearProgress-bar': { backgroundColor: palette.warning.main },
    backgroundColor: palette.warning.light,
  },
  error: {
    '& .MuiLinearProgress-bar': { backgroundColor: palette.error.main },
    backgroundColor: palette.error.light,
  },
};

type ScoreBreakdownCriteriaTableProperties = {
  criteria: ScoreCriterion[];
};

export const ScoreBreakdownCriteriaTable: React.FC<ScoreBreakdownCriteriaTableProperties> = ({ criteria }) => {
  return (
    // Таблица уже лежит внутри карточки `Paper` — своя рамка ей не нужна, нужен только скролл
    <ScrollableTable bordered={false} dataTest='score-breakdown-criteria-table' ariaLabel='Разбор итоговой оценки'>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                ...tableHeaderCellSx,
                color: lightPalette.text.muted,
                backgroundColor: lightPalette.background.default,
                py: 1.25,
                px: undefined,
              }}
            >
              Критерий
            </TableCell>
            <TableCell
              sx={{
                ...tableHeaderCellSx,
                color: lightPalette.text.muted,
                backgroundColor: lightPalette.background.default,
                py: 1.25,
                px: undefined,
                minWidth: 160,
              }}
            >
              Оценка
            </TableCell>
            <TableCell
              sx={{
                ...tableHeaderCellSx,
                color: lightPalette.text.muted,
                backgroundColor: lightPalette.background.default,
                py: 1.25,
                px: undefined,
                width: 72,
                textAlign: 'right',
              }}
            >
              Вес
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {criteria.map((criterion, index) => {
            const level = getScoreLevel(criterion.score, criterion.max_score);
            const barPercent = criterion.max_score > 0
              ? (criterion.score / criterion.max_score) * 100
              : 0;

            return (
              <TableRow key={index} sx={zebraRowSx}>
                {/* Name */}
                <TableCell
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: lightPalette.text.primary,
                    letterSpacing: '-0.01em',
                    borderBottom: `1px solid ${lightPalette.border.light}`,
                    py: 1.5,
                  }}
                >
                  {criterion.name}
                </TableCell>

                {/* Score + bar */}
                <TableCell
                  sx={{
                    borderBottom: `1px solid ${lightPalette.border.light}`,
                    py: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <LinearProgress
                      variant="determinate"
                      value={barPercent}
                      sx={{
                        flex: 1,
                        height: 6,
                        borderRadius: 3,
                        ...LEVEL_PROGRESS_SX[level],
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: 12,
                        fontWeight: 600,
                        color: LEVEL_COLORS[level],
                        minWidth: 36,
                        textAlign: 'right',
                        flexShrink: 0,
                      }}
                    >
                      {criterion.score}/{criterion.max_score}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Weight */}
                <TableCell
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: 12,
                    fontWeight: 500,
                    color: lightPalette.text.muted,
                    borderBottom: `1px solid ${lightPalette.border.light}`,
                    py: 1.5,
                    textAlign: 'right',
                  }}
                >
                  {Math.round(criterion.weight * 100)}%
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </ScrollableTable>
  );
};
